/**
 * "What happened today" — the header chip and the sheet it opens.
 *
 * On demand only, per the composer doc's "mostly when asked" rule: nothing
 * fetches until the owner taps the chip, and the chip itself renders nothing
 * — not even an empty slot — for a household without report_today_v1 on.
 * The sheet shows exactly what GET /v1/reports/today returns: counts and a
 * short exceptions list, never a task list.
 */
import { Icon } from '@iconify/react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, api } from '../api';
import { Sheet } from '../components/ui/Sheet';
import { t } from '../i18n';

interface DailyReportLine {
  operation_id: string;
  title: string;
  kind: 'done' | 'late' | 'reassigned';
  detail: string;
}

/** A planned shift nobody clocked in for today — one row per shift. */
interface DailyReportNoShow {
  shift_id: string;
  staff_user_id: string | null;
  staff_name: string | null;
  detail: string;
}

interface DailyReportData {
  date: string;
  counts: { done: number; late: number; waiting: number; reassigned: number; no_show: number };
  lines: DailyReportLine[];
  no_shows: DailyReportNoShow[];
}

const KIND_ICON: Record<DailyReportLine['kind'], string> = {
  done: 'solar:check-circle-bold',
  late: 'ph:warning-circle-bold',
  reassigned: 'ph:arrows-left-right-bold',
};

/** The header entry point, on the phone's chip bar (HomeHeader) and the
 *  laptop's shell bar (OwnerLayout) alike — `variant` picks which chrome to
 *  match, same split NotificationBell already draws. Self-contained: reads
 *  its own flag, same convention as every other flag-gated header widget
 *  reading /v1/flags itself rather than threading a prop down from the page
 *  that hosts it. */
export function DailyReportEntry({ variant = 'default' }: { variant?: 'default' | 'chip' }) {
  const flagsQ = useQuery({
    queryKey: ['/v1/flags'],
    queryFn: () => api<{ flags: Record<string, boolean> }>('/v1/flags'),
  });
  const [open, setOpen] = useState(false);

  if (flagsQ.data?.flags.report_today_v1 !== true) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('Today')}
        className={
          variant === 'chip'
            ? 'relative flex h-12 w-12 items-center justify-center rounded-[20px] border border-border bg-card text-foreground shadow-[0_4px_12px_rgba(0,0,0,0.04)] transition-all active:scale-95'
            : 'touch inline-flex items-center justify-center rounded-md px-2 text-foreground/80 hover:text-foreground'
        }
      >
        {variant === 'chip' ? (
          <Icon icon="ph:sparkle-fill" className="text-2xl opacity-80" aria-hidden />
        ) : (
          <Icon icon="ph:sparkle-fill" className="text-lg" aria-hidden />
        )}
      </button>
      {open && <DailyReportSheet onClose={() => setOpen(false)} />}
    </>
  );
}

function DailyReportSheet({ onClose }: { onClose: () => void }) {
  const reportQ = useQuery({
    queryKey: ['/v1/reports/today'],
    queryFn: () => api<DailyReportData>('/v1/reports/today'),
  });

  return (
    <Sheet title={t('Today')} onClose={onClose} tall closeOnBackdrop>
      {reportQ.isPending && (
        <p className="py-8 text-center text-sm font-medium text-muted-foreground">
          {t('Reading…')}
        </p>
      )}
      {reportQ.isError && (
        <p className="py-8 text-center text-sm font-bold text-urgent">
          {reportQ.error instanceof ApiError ? reportQ.error.message : t('Failed')}
        </p>
      )}
      {reportQ.data && (
        <div className="space-y-6">
          <div
            className={`grid gap-2 ${reportQ.data.counts.no_show > 0 ? 'grid-cols-5' : 'grid-cols-4'}`}
          >
            <CountTile label={t('Done')} value={reportQ.data.counts.done} />
            <CountTile label={t('Late')} value={reportQ.data.counts.late} tone="urgent" />
            <CountTile label={t('Waiting')} value={reportQ.data.counts.waiting} />
            <CountTile label={t('Reassigned')} value={reportQ.data.counts.reassigned} />
            {reportQ.data.counts.no_show > 0 && (
              <CountTile label={t('No-show')} value={reportQ.data.counts.no_show} tone="urgent" />
            )}
          </div>

          {reportQ.data.no_shows.length > 0 && (
            <div className="space-y-2">
              {reportQ.data.no_shows.map((ns) => (
                <div
                  key={ns.shift_id}
                  className="flex items-start gap-3 rounded-[18px] border border-border bg-card p-4"
                >
                  <Icon
                    icon="ph:user-minus-bold"
                    className="mt-0.5 shrink-0 text-lg text-urgent"
                    aria-hidden
                  />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-foreground" dir="auto">
                      {ns.staff_name ?? t('Planned shift')}
                    </p>
                    <p className="text-xs font-medium text-muted-foreground" dir="auto">
                      {ns.detail}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {reportQ.data.lines.length > 0 ? (
            <div className="space-y-2">
              {reportQ.data.lines.map((line) => (
                <div
                  key={`${line.operation_id}-${line.kind}`}
                  className="flex items-start gap-3 rounded-[18px] border border-border bg-card p-4"
                >
                  <Icon
                    icon={KIND_ICON[line.kind]}
                    className={`mt-0.5 shrink-0 text-lg ${line.kind === 'late' ? 'text-urgent' : 'text-primary'}`}
                  />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-foreground" dir="auto">
                      {line.title}
                    </p>
                    <p className="text-xs font-medium text-muted-foreground" dir="auto">
                      {line.detail}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            reportQ.data.no_shows.length === 0 && (
              <p className="py-4 text-center text-sm font-medium text-muted-foreground">
                {t('Nothing to flag today.')}
              </p>
            )
          )}
        </div>
      )}
    </Sheet>
  );
}

function CountTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'urgent';
}) {
  return (
    <div className="flex flex-col items-center gap-1 rounded-[16px] bg-secondary py-3">
      <span
        className={`text-xl font-bold tabular-nums ${tone === 'urgent' && value > 0 ? 'text-urgent' : 'text-foreground'}`}
      >
        {value}
      </span>
      <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">
        {label}
      </span>
    </div>
  );
}
